import { useEffect, useRef, useState } from 'react';
import type { EditorState } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import { ignoreKey } from '@realmery/realm-core';
import { realms } from '../db/client.ts';
import { keyLabel, keysOf } from '../shortcuts.ts';
import { addToMyDictionary, spellingChanged } from '../spelling/client.ts';
import { openThesaurus, thesaurus, type SynonymMenu, type ThesaurusTarget } from '../thesaurus/client.ts';
import type { Misspelling, ProofingScope } from './spellingPlugin.ts';

/**
 * The writing aids in the editors (docs/PARITY-STEP-6-PLAN.md sections 5 and 6), as the
 * desktop's `ThesaurusMenus` and its editors' Shift+F7: the word under the pointer or the
 * caret (or a short phrase selected, such as "gave up"), with the word before it, which
 * tells its form; a right-click menu with its synonyms already in that form; and the
 * Thesaurus panel opened on it. A replacement goes in only if the word is still there.
 */

/** A word, or up to four selected, in one paragraph: where it is, and the word before it. */
export interface WordSpot {
  readonly from: number;
  readonly to: number;
  readonly text: string;
  readonly before: string | null;
}

const WORD = /[\p{L}\p{N}'’-]/u;
const MAX_PHRASE_WORDS = 4;

/** The word before `offset` in a paragraph's text. */
function wordBefore(text: string, offset: number): string | null {
  const m = /([\p{L}'’]+)[^\p{L}\p{N}'’]*$/u.exec(text.slice(0, offset));
  return m ? m[1]! : null;
}

/** The selected phrase, or the word at `pos` (the caret's, by default). */
export function wordAt(state: EditorState, pos: number = state.selection.from): WordSpot | null {
  const { selection } = state;
  const $pos = state.doc.resolve(pos);
  const block = $pos.parent;
  if (!block.isTextblock) return null;
  const start = $pos.start();
  // Inline pictures and line breaks count one place each, so offsets match positions.
  const text = block.textBetween(0, block.content.size, undefined, '￼');
  if (!selection.empty && selection.$from.sameParent(selection.$to) && selection.$from.parent === block && pos >= selection.from && pos <= selection.to) {
    const phrase = text.slice(selection.from - start, selection.to - start);
    const words = phrase.trim().split(/\s+/u);
    if (phrase.trim() !== '' && words.length <= MAX_PHRASE_WORDS && !phrase.includes('￼')) {
      const lead = phrase.length - phrase.trimStart().length;
      const from = selection.from + lead;
      return { from, to: from + phrase.trim().length, text: phrase.trim(), before: wordBefore(text, from - start) };
    }
  }
  let a = pos - start;
  let b = a;
  while (a > 0 && WORD.test(text[a - 1]!)) a--;
  while (b < text.length && WORD.test(text[b]!)) b++;
  // Quotes and dashes around a word aren't part of it.
  while (a < b && /['’-]/u.test(text[a]!)) a++;
  while (b > a && /['’-]/u.test(text[b - 1]!)) b--;
  if (a === b || !/\p{L}/u.test(text.slice(a, b))) return null;
  return { from: start + a, to: start + b, text: text.slice(a, b), before: wordBefore(text, a) };
}

/** The panel's target: replacing the word if the text there hasn't changed since. */
export function targetFor(view: EditorView, spot: WordSpot): ThesaurusTarget {
  return {
    word: spot.text,
    before: spot.before,
    replace: (word) => {
      if (view.isDestroyed || spot.to > view.state.doc.content.size || view.state.doc.textBetween(spot.from, spot.to) !== spot.text) return false;
      view.dispatch(view.state.tr.insertText(word, spot.from, spot.to).scrollIntoView());
      view.focus();
      return true;
    },
  };
}

export interface MenuAt {
  readonly x: number;
  readonly y: number;
  readonly spot: WordSpot;
  /** The misspelling right-clicked (or reached with F7), if it's one. */
  readonly misspelling?: Misspelling | null;
}

/**
 * The right-click menu on a word: for a misspelling, what could replace it, Add to Realm
 * Dictionary, Add to My Dictionary and Ignore (the desktop's `ProofingOverlay` menu); then
 * its synonyms, the Thesaurus, and Cut and Copy. Shift+right-click gives the browser's own menu.
 */
export function WritingMenu({ view, at, proofing, onClose }: { view: EditorView; at: MenuAt; proofing?: ProofingScope | undefined; onClose: () => void }) {
  const [menu, setMenu] = useState<SynonymMenu | 'loading' | 'failed'>('loading');
  const box = useRef<HTMLDivElement>(null);
  const target = targetFor(view, at.spot);
  const keys = keysOf('writing.thesaurus').map(keyLabel).join(' or ');

  useEffect(() => {
    let live = true;
    thesaurus.menu(at.spot.text, at.spot.before).then(
      (m) => live && setMenu(m),
      () => live && setMenu('failed'),
    );
    return () => {
      live = false;
    };
  }, [at.spot.text, at.spot.before]);

  // Closes on a click elsewhere, a scroll or the window losing focus; the first item takes the keys.
  useEffect(() => {
    const away = (e: Event) => {
      if (!box.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', away, true);
    window.addEventListener('blur', onClose);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      window.removeEventListener('blur', onClose);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);
  useEffect(() => {
    box.current?.querySelector<HTMLButtonElement>('[role=menuitem]:not(:disabled)')?.focus();
  }, [menu]);

  const pick = (word: string) => {
    target.replace(word);
    onClose();
  };
  const miss = at.misspelling ?? null;
  /** Puts a suggestion in place of the misspelling, if it's still there. */
  const correct = (word: string) => {
    if (miss && view.state.doc.textBetween(miss.from, miss.to) === miss.issue.text) {
      view.dispatch(view.state.tr.insertText(word, miss.from, miss.to).scrollIntoView());
      view.focus();
    }
    onClose();
  };
  const remember = (work: () => Promise<unknown> | void) => {
    onClose();
    view.focus();
    void Promise.resolve(work()).then(spellingChanged);
  };
  const clip = (command: 'cut' | 'copy') => {
    view.focus();
    document.execCommand(command);
    onClose();
  };
  const shorten = (text: string) => (text.length <= 30 ? text : `${text.slice(0, 29)}…`);
  // Kept inside the window.
  const left = Math.max(4, Math.min(at.x, window.innerWidth - 280));
  const top = Math.max(4, Math.min(at.y, window.innerHeight - 360));

  return (
    <div
      ref={box}
      className="writing-menu"
      role="menu"
      aria-label={`Writing aids for “${at.spot.text}”`}
      style={{ left, top }}
      onKeyDown={(e) => {
        const items = [...(box.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]:not(:disabled)') ?? [])];
        const at = items.indexOf(document.activeElement as HTMLButtonElement);
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
          view.focus();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          items[(at + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length]?.focus();
        }
      }}
    >
      {miss && proofing && (
        <div role="group" aria-label="Spelling">
          {miss.issue.suggestions.length === 0 ? (
            <p className="writing-menu-note">No suggestions</p>
          ) : (
            miss.issue.suggestions.map((word) => (
              <button key={word} type="button" role="menuitem" className="writing-menu-word" onClick={() => correct(word)}>
                {word}
              </button>
            ))
          )}
          <hr />
          <button type="button" role="menuitem" onClick={() => remember(() => realms.addToRealmDictionary(proofing.file, miss.issue.text.trim()))}>
            Add “{miss.issue.text.trim()}” to Realm Dictionary
          </button>
          <button type="button" role="menuitem" onClick={() => remember(() => addToMyDictionary(miss.issue.text))}>
            Add to My Dictionary
          </button>
          <button type="button" role="menuitem" onClick={() => remember(() => realms.ignoreSpelling(proofing.file, proofing.scopeId, ignoreKey(miss.issue)))}>
            Ignore in This {proofing.noun}
          </button>
          <hr />
        </div>
      )}
      {menu === 'loading' ? (
        <p className="writing-menu-note">Synonyms (loading…)</p>
      ) : menu === 'failed' ? (
        <p className="writing-menu-note">Synonyms (thesaurus unavailable)</p>
      ) : menu.empty ? (
        <p className="writing-menu-note">No synonyms for “{shorten(menu.written)}”</p>
      ) : (
        <>
          {menu.groups.map((group) => (
            <div key={group.heading} role="group" aria-label={group.heading}>
              <p className="writing-menu-heading">{group.heading}</p>
              {group.words.map((word) => (
                <button key={word} type="button" role="menuitem" className="writing-menu-word" onClick={() => pick(word)}>
                  {word}
                </button>
              ))}
            </div>
          ))}
          {menu.opposites.length > 0 && (
            <div role="group" aria-label="opposites">
              <hr />
              <p className="writing-menu-heading">opposites</p>
              {menu.opposites.map((word) => (
                <button key={word} type="button" role="menuitem" className="writing-menu-word" onClick={() => pick(word)}>
                  {word}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      <hr />
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onClose();
          openThesaurus(target);
        }}
      >
        Thesaurus…{keys && <span className="writing-menu-key">{keys}</span>}
      </button>
      <hr />
      <button type="button" role="menuitem" onClick={() => clip('cut')}>
        Cut
      </button>
      <button type="button" role="menuitem" onClick={() => clip('copy')}>
        Copy
      </button>
      <p className="writing-menu-note">Paste with Ctrl+V. Shift+right-click for the browser's menu.</p>
    </div>
  );
}
